import React from 'react';
import BundleResultItem from "@app/react/components/BundleResultItem/BundleResultItem.jsx";
import {useEffect} from "react";
import Api from "@app/api/Api.js";
import InputChoice from "@app/react/components/InputChoice/InputChoice.jsx";

const ITEMS_PER_PAGE = 24

function parseHash() {
  const hash = window.location.hash.slice(1)
  if (!hash) return {}
  const params = new URLSearchParams(hash)
  const parseIds = key => { const v = params.get(key); return v ? v.split(',').filter(Boolean) : [] }
  return {
    type: params.get('type') || '',
    collections: parseIds('collections'),
    versions: parseIds('versions'),
    models: parseIds('models'),
    certifications: parseIds('certifications'),
    areas: parseIds('areas'),
  }
}

function buildHash(type, collectionIds, versionIds, modelIds, certificationIds, areaIds) {
  const params = new URLSearchParams()
  if (type) params.set('type', type)
  if (collectionIds.length) params.set('collections', collectionIds.join(','))
  if (versionIds.length) params.set('versions', versionIds.join(','))
  if (modelIds.length) params.set('models', modelIds.join(','))
  if (certificationIds.length) params.set('certifications', certificationIds.join(','))
  if (areaIds.length) params.set('areas', areaIds.join(','))
  const str = params.toString()
  return str ? '#' + str : window.location.pathname + window.location.search
}

export default function BundleCategorySearch({categoryId, categories, defaultTypeId = '', ecodesignCertifications = []}) {

  const initial = React.useMemo(parseHash, [])
  const [typeId, setTypeId] = React.useState(initial.type || defaultTypeId)
  const [collectionIds, setCollectionIds] = React.useState(initial.collections ?? [])
  const [modelIds, setModelIds] = React.useState(initial.models ?? [])
  const [versionIds, setVersionIds] = React.useState(initial.versions ?? [])
  const [certificationIds, setCertificationIds] = React.useState(initial.certifications ?? [])
  const [areaIds, setAreaIds] = React.useState(initial.areas ?? [])
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const [bundles, setBundles] = React.useState([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(0)
  const resultsRef = React.useRef(null)
  const selectedCategory = categories.find(category => category.id === parseInt(categoryId))

  function withPageReset(setter) {
    return (value) => { setter(value); setPage(0) }
  }

  function buildChoices(categoryData) {
    const choices = [];
    Object.keys(categoryData).forEach(id => {
      choices.push({
        value: categoryData[id].id,
        name: categoryData[id].name
      });
    });
    return choices;
  }

  useEffect(() => {
    history.replaceState(null, '', buildHash(typeId, collectionIds, versionIds, modelIds, certificationIds, areaIds))
  }, [typeId, collectionIds, modelIds, versionIds, certificationIds, areaIds])

  useEffect(() => {
    Api.get('api/bundles', {
      categoryIds: categoryId ? [categoryId] : null,
      typeIds: typeId ? [typeId] : null,
      collectionIds: collectionIds.length ? collectionIds : null,
      modelIds: modelIds.length ? modelIds : null,
      versionIds: versionIds.length ? versionIds : null,
      certificationIds: certificationIds.length ? certificationIds : null,
      areaIds: areaIds.length ? areaIds : null,
      offset: page * ITEMS_PER_PAGE,
    }).then(response => {
      setBundles(response.bundles)
      setTotal(response.total)
    })
  }, [typeId, collectionIds, modelIds, versionIds, certificationIds, areaIds, page])

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE)

  function handlePage(p) {
    setPage(p)
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const activeFiltersCount = collectionIds.length + versionIds.length + modelIds.length + certificationIds.length + areaIds.length

  useEffect(() => {
    document.body.classList.toggle('bundle-drawer-open', drawerOpen)
    return () => document.body.classList.remove('bundle-drawer-open')
  }, [drawerOpen])

  // La categoria corrente, espansa, è sempre la prima voce del menu.
  const menuCategories = [...categories].sort((a, b) => (b.id === selectedCategory.id) - (a.id === selectedCategory.id))

  const menu = (
    <div className='bundle-categories-menu'>
      {menuCategories.map(category =>
        <MenuItem key={category.id} category={category} currentCategoryId={parseInt(categoryId)} currentTypeId={typeId} onTypeChange={withPageReset(setTypeId)} />
      )}
      <div className='bundle-category-menu-item bundle-category-menu-external'>
        <a className='category-name' href='https://www.urbantime.it/' target='_blank' rel='noopener'>{Drupal.tt('outdoor')}</a>
      </div>
    </div>
  )

  const filters = (
    <>
      <InputChoice label={Drupal.tt('collezioni')} value={collectionIds} onChange={withPageReset(setCollectionIds)} choices={buildChoices(selectedCategory.collections)} multiple={true} />
      <InputChoice label={Drupal.tt('versioni')} value={versionIds} onChange={withPageReset(setVersionIds)} choices={buildChoices(selectedCategory.versions)} multiple={true} />
      <InputChoice label={Drupal.tt('stili')} value={modelIds} onChange={withPageReset(setModelIds)} choices={buildChoices(selectedCategory.models)} multiple={true} />
      {ecodesignCertifications.length > 0 && (
        <InputChoice label={Drupal.tt('ecodesign')} value={certificationIds} onChange={withPageReset(setCertificationIds)} choices={buildChoices(ecodesignCertifications)} multiple={true} />
      )}
      {selectedCategory.areas?.length > 0 && (
        <InputChoice label={Drupal.tt('ambiti')} value={areaIds} onChange={withPageReset(setAreaIds)} choices={buildChoices(selectedCategory.areas)} multiple={true} />
      )}
    </>
  )

  return <div className='bundle-category-search'>
    <button type='button' className='bundle-filters-toggle d-lg-none' onClick={() => setDrawerOpen(true)}>
      {Drupal.tt('Filtri')}{activeFiltersCount > 0 && <span className='numSelected'> ({activeFiltersCount})</span>}
    </button>

    <div className={'bundle-filters-drawer d-lg-none' + (drawerOpen ? ' open' : '')} role='dialog' aria-modal='true'>
      <div className='bundle-filters-drawer-header'>
        <span>{Drupal.tt('Filtri')}</span>
        <button type='button' className='bundle-filters-drawer-close' aria-label={Drupal.tt('Chiudi')} onClick={() => setDrawerOpen(false)}>×</button>
      </div>
      <div className='bundle-filters-drawer-body'>
        {menu}
        <div className='bundle-filters-drawer-filters'>{filters}</div>
      </div>
      <div className='bundle-filters-drawer-footer'>
        <button type='button' className='btn-link' onClick={() => setDrawerOpen(false)}>
          {Drupal.tt('Mostra risultati')} ({total})
        </button>
      </div>
    </div>

    <div className='row'>
      <div className='col-lg-2 d-none d-lg-block'>
        {menu}
      </div>
      <div className='col-lg-10'>
        <div className='d-none d-lg-flex bundle-category-search-filters'>
          {filters}
        </div>

        <div className='row' ref={resultsRef}>
          {bundles.map(bundle => <BundleResultItem key={bundle.id} bundle={bundle} linkToProduct={true} className='col-md-4 col-xl-3' />)}
        </div>

        <Pagination page={page} totalPages={totalPages} onPage={handlePage} />
      </div>
    </div>
  </div>
}


function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null

  const pages = []
  let prev

  for (let i = 0; i < totalPages; i++) {
    if (i === 0 || i === totalPages - 1 || (i >= page - 2 && i <= page + 2)) {
      if (prev !== undefined && prev < i - 1) pages.push(null)
      pages.push(i)
      prev = i
    }
  }

  return (
    <div className='bundle-pagination'>
      <button className='bundle-pagination-first' disabled={page === 0} onClick={() => onPage(0)} aria-label={Drupal.tt('Prima pagina')}></button>
      <button className='bundle-pagination-prev' disabled={page === 0} onClick={() => onPage(page - 1)} aria-label={Drupal.tt('Pagina precedente')}></button>
      {pages.map((p, idx) =>
        p === null
          ? <span key={`e${idx}`} className='bundle-pagination-ellipsis'>…</span>
          : <button key={p} className={'bundle-pagination-page' + (p === page ? ' active' : '')} onClick={() => onPage(p)}>{p + 1}</button>
      )}
      <button className='bundle-pagination-next' disabled={page === totalPages - 1} onClick={() => onPage(page + 1)} aria-label={Drupal.tt('Pagina successiva')}></button>
      <button className='bundle-pagination-last' disabled={page === totalPages - 1} onClick={() => onPage(totalPages - 1)} aria-label={Drupal.tt('Ultima pagina')}></button>
    </div>
  )
}

function MenuItem({category, currentCategoryId, currentTypeId, onTypeChange}) {
  const isCurrentCategory = category.id === currentCategoryId
  const [open, setOpen] = React.useState(isCurrentCategory)

  function toggleOpen() {
    setOpen(prev => !prev)
  }

  function handleSelect(evt, typeId) {
    if(isCurrentCategory) {
      evt.preventDefault()
      onTypeChange(typeId)
    }
  }

  // Le altre categorie non si espandono: portano a tutti i loro prodotti.
  if(!isCurrentCategory) {
    return <div className='bundle-category-menu-item bundle-category-menu-link'>
      <a className='category-name' href={category.url}>{category.title}</a>
    </div>
  }

  return <div className={'bundle-category-menu-item' + (open ? ' open' : '') + ' active'}>
    <div className='category-name' onClick={toggleOpen}>{category.title}</div>
    <div className='category-types'>
      <a href={category.url} className={(isCurrentCategory && currentTypeId===null) ? 'active':''}  onClick={evt => handleSelect(evt, null)}><i>{Drupal.tt('tutti')}</i></a>
      {category.types.map(type =>
        <a key={type.id} href={category.url + '?frame=' + type.id} className={currentTypeId===type.id ? 'active':''} onClick={evt => handleSelect(evt, type.id)}>
        {type.name}
        </a>
      )}
    </div>
  </div>
}
